import { AfterViewInit, Component, inject, OnInit, signal, WritableSignal } from '@angular/core';
import { ConfirmationService, LazyLoadEvent, MessageService } from 'primeng/api';
import { SERVICE_ENDPOINTS } from '../../../core/configs/service-endpoints';
import { ServiceResult } from '../../../core/interfaces/service-result';
import { FileService } from '../../../core/services/file.service';
import { HelperService } from '../../../core/services/helper.service';
import { I18nService } from '../../../core/services/i18n.service';
import { UserSessionSettingsProvider } from '../../../core/services/user-session-settings-provider.service';
import { SpsTableComponent } from '../../../custom/components/sps-table/sps-table.component';
import { TableConfig } from '../../../custom/interfaces/sps-table/table-config';
import { TableListProvider } from '../../../custom/provider/table-list-provider';
import { TableConfigService } from '../../../custom/services/table-config.service';
import { TableControlService } from '../../../custom/services/table-control.service';
import { LayoutService } from '../../../layout/services/layout.service';
import { TransactionCreate } from '../../dtos/transaction/transaction-create';
import { PaymentMethodName } from '../../enums/payment/payment-method-name';
import { TransactionData } from '../../interfaces/transaction/transaction-data';
import { TransactionDetailsDialog } from '../../interfaces/transaction/transaction-details-dialog';
import { TransactionDataToTransactionCreateMapperService } from '../../mapper/transaction/transaction-data-to-transaction-create-mapper.service';
import { TransactionToTransactionViewDataMapperService } from '../../mapper/transaction/transaction-to-transaction-view-data-mapper.service';
import { TransactionDataService } from '../../services/transaction-data.service';
import { TransactionHandlingService } from '../../services/transaction-handling.service';
import { BaseComponent } from './base.component';

@Component({
  template: '',
})
export abstract class BaseTransactionViewComponent extends BaseComponent implements OnInit, AfterViewInit {

  protected abstract tableConfigKeyName: string;
  protected abstract tableRowsKeyName: string;
  protected abstract tableStateKeyName: string;
  public abstract transactionViewMode: 'TENANT' | 'ADMIN';
  transactionDetailsId: string;
  transactionDetailsHeader: string;
  readonly showTransactionError = signal<boolean>(false);
  readonly transactionErrorMessage = signal<String>('');


  /**
   * table-config for transactions view -> SPS_TRANSACTIONS_VIEW.json
   */
  readonly tableConfig: WritableSignal<TableConfig> = signal(null);
  totalRecords: number = 0;
  rows: number = 20;
  scrollHeightOffset: number = 146;
  minScrollHeight: number = 160;
  tableListProvider: TableListProvider;
  selectedRowData: TransactionData = null;

  /**
   * SPS-Table-Component -> Reference
   * Initialized in HTML by event
   * needed for the data get event of the table in the polling-service
   * component.tableComponent.getTablePageData(component.lastLazyLoadingEvent);
   */
  tableComponent: SpsTableComponent = null;

  /** Options and Models */
  stateOptions = [
    {value: 'TEST', label: 'Test'},
    {value: 'LIVE', label: 'Live'},
  ];

  // changes to LIVE on stakeholder request 05.10.2026
  // currentState: string = 'TEST';
  currentState: string = 'LIVE';

  /** Details Dialog Initialization-Object */
  transactionDetailsDialog: TransactionDetailsDialog = {visible: false};

  /**
   * Param for  Data-Refresh-Polling
   * extended params see implementation in the child-components
   */
  lastLazyLoadingEvent: LazyLoadEvent = null;


  public i18n = inject(I18nService);
  protected messageService = inject(MessageService);
  protected confirmationService = inject(ConfirmationService);
  public layoutService = inject(LayoutService);
  protected helper =  inject(HelperService);
  protected userSessionSettingsProvider = inject(UserSessionSettingsProvider);
  protected tableConfigService = inject(TableConfigService);
  public tableControlService = inject(TableControlService);
  protected transactionDataService = inject(TransactionDataService);
  protected transactionHandlingService = inject(TransactionHandlingService);
  protected transactionDataMapperService = inject(TransactionToTransactionViewDataMapperService);
  protected transactionCreateMapper = inject(TransactionDataToTransactionCreateMapperService);

  protected fileService = inject(FileService);

  async ngOnInit() {
    this.tableListProvider = new TableListProvider(this.i18n);
    this.tableConfig.set(await this.tableConfigService.getTableConfig(this.tableConfigKeyName, false));
    this.rows = this.tableConfig().rows;
    if (this.userSessionSettingsProvider.hasValue(this.tableRowsKeyName)) {
      this.rows = this.userSessionSettingsProvider.get(this.tableRowsKeyName);
    }
    if (this.userSessionSettingsProvider.hasValue(this.tableStateKeyName)) {
      this.currentState = this.userSessionSettingsProvider.get(this.tableStateKeyName);
    }
  }

  ngAfterViewInit() {
    /**
     * Some controls need refresh after language-changes
     */
    this.subscriptions.add(
      this.i18n.languageChanged.subscribe({
        next: value => {
          this.tableControlService.refreshTableConfig.set(true);
          this.refreshControls = true;
          setTimeout(() => {
            this.tableControlService.refreshTableConfig.set(false);
            this.refreshControls = false;
          }, 10);
        }
      })
    );
  }

  /**
   * Table-data-Fetched subscription
   * @param serviceResult
   */
  onDataFetched(serviceResult: ServiceResult) {
    this.totalRecords = serviceResult.totalElements;
  }

  /**
   * Close Transactions Details Dialog
   */
  closeDetailsDialog() {
    this.transactionErrorMessage.set('');
    this.showTransactionError.set(false);
    this.transactionDetailsDialog.visible = false;
    this.selectedRowData = null;
    this.tableControlService.unselectTableRows.set(true);
  }

  /**
   * if row was selected - open details dialog for specific paymentMethod
   * @param transactionData
   */
  onSelectionChanged(transactionData: TransactionData) {
    this.selectedRowData = null;
    if (transactionData) {
      this.selectedRowData = transactionData;
      /**
       * get transaction data and history from backend
       */
      this.transactionDetailsId = this.selectedRowData.id;
      this.transactionDetailsHeader = this.i18n?.i18nLng()['TRANSACTION'] + ': ' + this.transactionDetailsId
      this.transactionDetailsDialog = {
        type: PaymentMethodName[this.selectedRowData.paymentMethod],
        visible: true
      };
    }
  }

  /**
   * Clear all Table Filters and Sorting
   * @protected
   */
  protected clearTableFilters() {
    this.tableControlService.clearTableFilters.set(true);
  }

  /**
   * Refresh table when status changed
   */
  tableStatusChanged() {
    setTimeout(() => {
      this.closeDetailsDialog();
      this.tableControlService.refreshTableConfig.set(true);
      setTimeout(() => {
        this.tableControlService.refreshTableConfig.set(false);
        this.userSessionSettingsProvider.set(this.tableStateKeyName, this.currentState);
      }, 100);
    }, 100);
  }

  /**
   * Rows changed by user
   * @param newRowCount
   */
  rowsCountChanged(newRowCount: number) {
    this.rows = newRowCount;
    this.userSessionSettingsProvider.set(this.tableRowsKeyName, this.rows);
  }

  /**
   * History transaction was selected in the details dialog
   * change the dialog title to the new ID
   * @param transactionId
   * @protected
   */
  protected onTransactionIdChanged(transactionId: string) {
    this.transactionDetailsId = transactionId;
    this.transactionDetailsHeader = this.i18n?.i18nLng()['TRANSACTION'] + ': ' + this.transactionDetailsId
  }

  /**
   * Capture event was fired
   * confirm and process the capture
   * @param transactionData
   * @protected
   */
  protected onProcessCapture(transactionData: TransactionData) {
    const formattedAmount = new Intl.NumberFormat("de-DE", {
      style: "currency",
      currency: transactionData.currency,
    }).format(transactionData.amount / 100);
    this.confirmationService.confirm({
      position: 'center',
      message: (this.i18n.i18nLng()['CONFIRMCAPTURE']).replace("#1", formattedAmount).replace("#2", transactionData.id),
      header: this.i18n.i18nLng()['CONFIRMATION'],
      closable: true,
      closeOnEscape: true,
      icon: 'pi pi-question-circle',
      acceptButtonProps: {
        label: this.i18n.i18nLng()['CAPTUREBUTTON']
      },
      rejectButtonProps: {
        label: this.i18n.i18nLng()['CANCEL'],
        outlined: true,
        severity: 'secondary'
      },
      accept: () => {
        this.layoutService.progressDialogTitle = this.i18n.i18nLng()['PROCESSINGTRANSACTION'];
        this.layoutService.showProgressSpinner = true;
        this.layoutService.showProgressDialog = true;

        const transactionCreate: TransactionCreate = this.transactionCreateMapper.map(transactionData, 'CAPTURE');

        this.transactionHandlingService.createChildTransaction(SERVICE_ENDPOINTS.CREATE_TRANSACTION, 'capture', transactionData.id, transactionCreate).subscribe({
          next: result => {
            this.layoutService.showProgressDialog = false;
            if (result.successful) {
              this.closeDetailsDialog();
              this.messageService.add({
                life: 3500,
                severity: 'success',
                summary: this.i18n.i18nLng()['CAPTURE'],
                detail: this.i18n.i18nLng()['TRANSACTIONSUCCESSFUL_MSG']
              });
            } else {
              this.transactionErrorMessage.set(this.i18n.i18nLng()['PROCESSTRANSACTIONERROR'] + ': ' + result.message);
              this.showTransactionError.set(true);
            }
          },
          error: err => {
            this.layoutService.showProgressDialog = false;
            this.transactionErrorMessage.set(this.i18n.i18nLng()['PROCESSTRANSACTIONERROR'] + ': ' + err.message);
            this.showTransactionError.set(true);
          }
        })
      }
    });
  }

  /**
   * void event was fired
   * confirm and process the void
   * @param transactionData
   * @protected
   */
  protected onProcessVoid(transactionData: TransactionData) {
    const formattedAmount = new Intl.NumberFormat("de-DE", {
      style: "currency",
      currency: transactionData.currency,
    }).format(transactionData.amount / 100);
    this.confirmationService.confirm({
      position: 'center',
      message: (this.i18n.i18nLng()['CONFIRMVOID']).replace("#1", this.i18n.i18nLng()['VOID_' + transactionData.type]).replace("#2", formattedAmount).replace("#3", transactionData.id),
      header: this.i18n.i18nLng()['CONFIRMATION'],
      closable: true,
      closeOnEscape: true,
      icon: 'pi pi-question-circle',
      acceptButtonProps: {
        label: this.i18n.i18nLng()['CANCELLATIONBUTTON']
      },
      rejectButtonProps: {
        label: this.i18n.i18nLng()['CANCEL'],
        outlined: true,
        severity: 'secondary'
      },
      accept: () => {
        this.layoutService.progressDialogTitle = this.i18n.i18nLng()['PROCESSINGTRANSACTION'];
        this.layoutService.showProgressSpinner = true;
        this.layoutService.showProgressDialog = true;

        const transactionCreate: TransactionCreate = this.transactionCreateMapper.map(transactionData, 'VOID');

        this.transactionHandlingService.createChildTransaction(SERVICE_ENDPOINTS.CREATE_TRANSACTION, 'void', transactionData.id, transactionCreate).subscribe({
          next: result => {
            this.layoutService.showProgressDialog = false;
            if (result.successful) {
              this.closeDetailsDialog();
              this.messageService.add({
                life: 3500,
                severity: 'success',
                summary: this.i18n.i18nLng()['VOID'],
                detail: this.i18n.i18nLng()['TRANSACTIONSUCCESSFUL_MSG']
              });
            } else {
              this.transactionErrorMessage.set(this.i18n.i18nLng()['PROCESSTRANSACTIONERROR'] + ': ' + result.message);
              this.showTransactionError.set(true);
            }
          },
          error: err => {
            this.layoutService.showProgressDialog = false;
            this.transactionErrorMessage.set(this.i18n.i18nLng()['PROCESSTRANSACTIONERROR'] + ': ' + err.message);
            this.showTransactionError.set(true);
          }
        })
      }
    });
  }
}
